import { Outlet, NavLink, useLocation } from 'react-router-dom';
import styles from './Layout.module.css';
import {
  CopyRegular,
  SignOutRegular,
  DataBarVerticalRegular,
  PersonNoteRegular,
  DocumentOnePageRegular,
  BookQuestionMarkRegular,
  BookLetterRegular,
  SettingsRegular,
} from '@fluentui/react-icons';
import { Avatar } from '@fluentui/react-components';
import { Dialog, Stack, TextField } from '@fluentui/react';
import { useEffect, useMemo, useState } from 'react';
import { useUser } from '../../auth/AuthProvider';
import { useLogout } from '../../auth';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { appConfig } from '../../appConfig';
import { useIsAuthenticated, useAccount } from '@azure/msal-react';
import { isFeatureEnabled } from '../../config/featureFlags';
import { useIntl } from 'react-intl';

const Layout = () => {
  const intl = useIntl();
  const showRidiculousShareWidget = false;
  const [isSharePanelOpen, setIsSharePanelOpen] = useState<boolean>(false);
  const [copyClicked, setCopyClicked] = useState<boolean>(false);
  const [copyText, setCopyText] = useState<string>('Copy URL');
  const logout = useLogout();
  const { user } = useUser();
  const username = user.name || user.username || 'Guest';
  const groupName = useMemo(() => {
    const roles = user.roles || [];
    return roles.includes('Task.Write')
      ? 'Admin'
      : roles.includes('User.Write')
        ? 'Consultant'
        : roles.includes('User.Read')
          ? 'Candidate'
          : 'Guest';
  }, [user.roles]);
  const { pathname } = useLocation();
  const [isShiftHeld, setIsShiftHeld] = useState(false);
  const account = useAccount();
  const isAdminOverride = localStorage.getItem('isAdmin') === 'true';
  const roles = account?.idTokenClaims?.roles || [];
  const isAdmin = isAdminOverride || roles.includes('Task.Write');

  const handleShareClick = () => {
    setIsSharePanelOpen(true);
  };

  const handleSharePanelDismiss = () => {
    setIsSharePanelOpen(false);
    setCopyClicked(false);
    setCopyText('Copy URL');
  };

  const handleCopyClick = () => {
    navigator.clipboard.writeText(window.location.href);
    setCopyClicked(true);
  };

  useEffect(() => {
    if (copyClicked) {
      setCopyText('Copied URL');
    }
  }, [copyClicked]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Shift') {
        setIsShiftHeld(true);
      }
    };

    const handleKeyUp = (e: KeyboardEvent) => {
      if (e.key === 'Shift') {
        setIsShiftHeld(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, []);

  const toggleAdminMode = () => {
    const currentValue = localStorage.getItem('isAdmin') === 'true';
    localStorage.setItem('isAdmin', (!currentValue).toString());
    window.location.reload();
  };

  const showClientProposal = isAdmin && isFeatureEnabled('clientProposal');
  const showCoverLetter = isAdmin && isFeatureEnabled('coverLetter');
  const showInterviewQuestions = isAdmin;
  const showMasterLists = isAdmin;

  return (
    <div
      className={styles.layout}
      style={{ backgroundImage: 'url(/bg.png)', backgroundSize: 'cover', backgroundRepeat: 'repeat' }}
    >
      <header className={styles.header} role={'banner'}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            marginLeft: '2rem',
            marginRight: '3rem',
            paddingTop: '1.5rem',
            paddingBottom: '1.5rem',
          }}
        >
          <NavLink
            to="/"
            className={({ isActive }) =>
              isActive ? `${styles.headerTitleContainer} ${styles.activeLink}` : styles.headerTitleContainer
            }
          >
            <img src="/shk_logo.png" aria-hidden="true" style={{ height: '30px', objectFit: 'contain' }} />
          </NavLink>
          <Stack horizontal verticalAlign="center" style={{ justifyContent: 'center' }}>
            {showClientProposal && (
              <NavLink
                to="/clientproposal"
                className={({ isActive }) =>
                  isActive
                    ? `${styles.mainMenuItem} ${styles.activeLink}`
                    : `${styles.mainMenuItem} ${styles.inactiveLink}`
                }
              >
                <DataBarVerticalRegular style={{ marginRight: '0.5rem' }} />
                <span className={`${styles.activeLinkText} ${styles.thinText}`}>Client Proposal</span>
              </NavLink>
            )}
            {showCoverLetter && (
              <NavLink
                to="/coverletter"
                className={({ isActive }) =>
                  isActive
                    ? `${styles.mainMenuItem} ${styles.activeLink}`
                    : `${styles.mainMenuItem} ${styles.inactiveLink}`
                }
              >
                <BookLetterRegular style={{ marginRight: '0.5rem' }} />
                <span className={`${styles.activeLinkText} ${styles.thinText}`}>Cover Letter</span>
              </NavLink>
            )}
            <NavLink
              to="/candidatereport"
              className={({ isActive }) =>
                isActive
                  ? `${styles.mainMenuItem} ${styles.activeLink}`
                  : `${styles.mainMenuItem} ${styles.inactiveLink}`
              }
            >
              <PersonNoteRegular style={{ marginRight: '0.5rem' }} />
              <span className={`${styles.activeLinkText} ${styles.thinText}`}>Candidate Report</span>
            </NavLink>
            <NavLink
              to="/trafficlightreport"
              className={({ isActive }) =>
                isActive
                  ? `${styles.mainMenuItem} ${styles.activeLink}`
                  : `${styles.mainMenuItem} ${styles.inactiveLink}`
              }
            >
              <DocumentOnePageRegular style={{ marginRight: '0.5rem' }} />
              <span className={`${styles.activeLinkText} ${styles.thinText}`}>Traffic Light</span>
            </NavLink>
            {showInterviewQuestions && (
              <NavLink
                to="/interviewquestions"
                className={({ isActive }) =>
                  isActive
                    ? `${styles.mainMenuItem} ${styles.activeLink}`
                    : `${styles.mainMenuItem} ${styles.inactiveLink}`
                }
              >
                <BookQuestionMarkRegular style={{ marginRight: '0.5rem' }} />
                <span className={`${styles.activeLinkText} ${styles.thinText}`}>Interview Questions</span>
              </NavLink>
            )}
            {showMasterLists && (
              <NavLink
                to="/admin/consultantbios"
                className={_ =>
                  pathname.includes('admin/')
                    ? `${styles.mainMenuItem} ${styles.activeLink}`
                    : `${styles.mainMenuItem} ${styles.inactiveLink}`
                }
              >
                <SettingsRegular style={{ marginRight: '0.5rem' }} />
                <span className={`${styles.activeLinkText} ${styles.thinText}`}>
                  {intl.formatMessage({ id: 'layout.navigation.admin' })}
                </span>
              </NavLink>
            )}
          </Stack>
          <div
            style={{ cursor: 'pointer' }}
            tabIndex={0}
            onClick={handleShareClick}
            onKeyDown={e => (e.key === 'Enter' || e.key === ' ' ? handleShareClick() : null)}
          >
            <Avatar name={username} style={{ color: '#fff', background: '#0F6CBD', borderRadius: '16px' }} />
          </div>
        </div>
      </header>
      <div className={styles.container}>
        <Stack horizontal className={styles.chatRoot}>
          <Outlet />
        </Stack>
      </div>
      <Dialog
        onDismiss={handleSharePanelDismiss}
        hidden={!isSharePanelOpen}
        styles={{
          main: [
            {
              selectors: {
                ['@media (min-width: 480px)']: {
                  maxWidth: '600px',
                  background: '#FFFFFF',
                  boxShadow: '0px 14px 28.8px rgba(0, 0, 0, 0.24), 0px 0px 8px rgba(0, 0, 0, 0.2)',
                  borderRadius: '8px',
                  maxHeight: '200px',
                  minHeight: showRidiculousShareWidget ? '200px' : '120px',
                },
              },
            },
          ],
        }}
        dialogContentProps={{
          title: '',
          showCloseButton: true,
        }}
      >
        <Stack verticalAlign="center" style={{ gap: '8px' }}>
          <div>
            <strong>{username}</strong> ({groupName})
          </div>
          <div
            className={styles.copyButtonContainer}
            role="button"
            tabIndex={0}
            aria-label="Logout"
            onClick={logout}
            onKeyDown={e => (e.key === 'Enter' || e.key === ' ' ? logout() : null)}
          >
            <SignOutRegular className={styles.copyButton} />
            <span className={styles.copyButtonText}>Logout</span>
          </div>
          {isShiftHeld && (
            <div
              className={styles.copyButtonContainer}
              role="button"
              tabIndex={0}
              aria-label="Toggle Admin"
              onClick={toggleAdminMode}
              onKeyDown={e => (e.key === 'Enter' || e.key === ' ' ? toggleAdminMode() : null)}
            >
              <SettingsRegular className={styles.copyButton} />
              <span className={styles.copyButtonText}>
                {localStorage.getItem('isAdmin') === 'true' ? 'Disable Admin Mode' : 'Enable Admin Mode'}
              </span>
            </div>
          )}
          {showRidiculousShareWidget && (
            <>
              <hr />
              <div>Share ...</div>
              <Stack horizontal verticalAlign="center" style={{ gap: '8px' }}>
                <TextField className={styles.urlTextBox} defaultValue={window.location.href} readOnly />
                <div
                  className={styles.copyButtonContainer}
                  role="button"
                  tabIndex={0}
                  aria-label="Copy"
                  onClick={handleCopyClick}
                  onKeyDown={e => (e.key === 'Enter' || e.key === ' ' ? handleCopyClick() : null)}
                >
                  <CopyRegular className={styles.copyButton} />
                  <span className={styles.copyButtonText}>{copyText}</span>
                </div>
              </Stack>
            </>
          )}
        </Stack>
      </Dialog>
      <div
        style={{
          position: 'fixed',
          bottom: '10px',
          right: '10px',
          color: 'gray',
          padding: '5px 10px',
          borderRadius: '5px',
          fontSize: '12px',
        }}
      >
        {appConfig.app.version}
      </div>
      <ToastContainer style={{ zIndex: 9999999 }} />
    </div>
  );
};

export default Layout;
